/**
 * Barrierefreiheits-Korrekturen fuer Widgets von Fremd-Apps.
 *
 * Diese Datei repariert Markup, das NICHT aus dem Theme stammt und das wir
 * deshalb nicht an der Quelle aendern koennen (App-Erweiterungen liefern ihr
 * JS von cdn.shopify.com/extensions/...). Jede Korrektur steht hier nur mit
 * Nachweis, was genau kaputt ist und warum die Reparatur zulaessig ist.
 *
 * Inhalt (jede mit eigenem Messnachweis an ihrem Abschnitt):
 *   1) Judge.me-Reiterleiste: aria-controls zeigt ins Leere
 *   2) Judge.me-Reiter-Dialog verdeckt das Schreib-Modal
 *   3) Judge.me-Bewertungs-Lasche: englisches aria-label (gemessen 24.09.:
 *      „Click to open Judge.me floating reviews tab“ auf „★ Bewertungen“)
 *   4) Judge.me-Karussell im Fuss: Fokus auf Karten ausserhalb des sichtbaren
 *      Bereichs (Q-12, Phase D Teil 2, P5; gemessen 08.10.: 28 Kartenlinks +
 *      2 Pfeile fokussierbar, sichtbar 1 Karte bei 390 px, 4 bei 1440 px)
 *
 * Alle Abschnitte laufen nach dem Ereignis ap:spaet-geladen (assets/ap-spaet.js,
 * spaetes Laden der Anbieter) erneut an.
 *
 * ---------------------------------------------------------------------------
 * 1) Judge.me — Reiterleiste des Bewertungs-Widgets ("jdgm-tablist")
 * ---------------------------------------------------------------------------
 * Judge.me rendert (Widget-Revamp, Vue):
 *
 *   div.jm-review-widget__header-and-body
 *     div.jm-review-widget__header
 *       div.jdgm-tabs
 *         div.jdgm-tablist
 *           div.jdgm-tablist__viewport
 *             div.jdgm-tablist__list[role=tablist]
 *               button#jdgm-tab_product_reviews[role=tab][aria-selected=true]
 *                     [aria-controls="jdgm-tabpanel_product_reviews"]
 *               button#jdgm-tab_shop_reviews[role=tab][aria-selected=false]
 *                     [aria-controls="jdgm-tabpanel_shop_reviews"]
 *           div.jdgm-tablist__content   <- NUR die Sortier-/Filterleiste
 *     div.jm-review-widget__body        <- der Inhalt des Reiters
 *
 * Die beiden `aria-controls`-Ziele existieren im DOM NICHT: `jdgm-tabpanel_*`
 * wird nirgends vergeben, und kein Element traegt `role="tabpanel"`. Ein ins
 * Leere zeigendes `aria-controls` ist ein harter Verstoss gegen
 * `aria-valid-attr-value` — Lighthouse und die Shopify-Pruefung
 * "Zugaenglichkeit von KI-Agenten" melden ihn auf jeder Produktseite.
 * Screenreader und Agenten finden den Reiterinhalt nicht: der Reiter
 * verspricht einen Bereich, den es nicht gibt.
 *
 * WELCHER Bereich der Inhalt ist, wurde am Livesystem gemessen (Reiterwechsel
 * "Produktbewertungen" -> "Shop-Bewertungen", Knoten vorher/nachher markiert):
 *   .jm-review-widget__body   derselbe Knoten, 374 px -> 1126 px  = getauscht
 *   .jdgm-tablist__content    derselbe Knoten,  28 px ->   28 px  = unveraendert
 * Der Name `__content` traegt also nicht, was er verspricht: dort steht die
 * Sortierleiste. Ziel ist `.jm-review-widget__body` (Rueckfall: `__content`).
 *
 * Es gibt genau EINEN solchen Bereich, der beim Reiterwechsel ausgetauscht
 * wird (Single-Panel-Muster). Nach ARIA APG gehoert deshalb an diesen Bereich
 * die `id`, auf die der AKTIVE Reiter zeigt, plus `role="tabpanel"` und
 * `aria-labelledby` auf den aktiven Reiter. Genau das stellen wir her.
 * Bleibt bei einem inaktiven Reiter ein Verweis ins Leere, wird `aria-controls`
 * entfernt (bei `role="tab"` ist das Attribut optional) und der urspruengliche
 * Wert in `data-ap-tabpanel` gesichert, damit er beim Aktivieren dieses
 * Reiters wieder gesetzt werden kann.
 *
 * axe wertet nur den aktiven Reiter als Verstoss (`aria-selected="false"`
 * schaltet die Pruefung ab, siehe axe-core `ariaValidAttrValueEvaluate`) —
 * nach einem Klick auf den zweiten Reiter waere also DIESER der Verstoss.
 * Die Reparatur laeuft deshalb dauerhaft ueber einen MutationObserver mit und
 * nicht nur einmal beim Laden.
 *
 * Selbstentschaerfend: Sobald Judge.me die Panels selbst vergibt, findet
 * `document.getElementById()` ein fremdes Element und wir fassen nichts an.
 */
(function () {
  'use strict';

  // Notbremse gegen eine Endlosschleife, falls Vue ein Attribut zurueckschreibt,
  // das wir gerade entfernt haben. Normalbetrieb: ~4 Schreibvorgaenge beim
  // Laden, ~6 je Reiterwechsel — das Budget traegt Hunderte Wechsel, eine
  // echte Schleife laeuft es dagegen in Sekunden leer und schaltet ab.
  var WRITE_BUDGET = 2000;
  var writes = 0;
  var scheduled = false;

  function write(fn) {
    if (writes >= WRITE_BUDGET) return false;
    writes += 1;
    fn();
    return true;
  }

  function wantedPanelId(tab) {
    return tab.getAttribute('aria-controls') || tab.getAttribute('data-ap-tabpanel') || '';
  }

  /**
   * Das Widget, zu dem diese Reiterleiste gehoert. Wichtig, weil Judge.me sein
   * Widget mehrfach in die Seite haengen kann — nach jedem Warenkorb-Update
   * landet eine zweite Kopie im Warenkorb-Schubkasten.
   */
  function widgetScope(list) {
    return (
      list.closest('.jm-review-widget__header-and-body') ||
      list.closest('.jm-review-widget') ||
      list.closest('.jdgm-review-widget')
    );
  }

  /** Den Inhaltsbereich DIESER Reiterleiste finden. */
  function findPanel(list, scope) {
    var panel = scope ? scope.querySelector('.jm-review-widget__body') : null;
    if (!panel) panel = list.querySelector('.jdgm-tablist__content');
    // Ein Bereich, der die Reiter selbst enthaelt, waere sein eigenes Panel.
    if (panel && panel.contains(list)) panel = null;
    return panel;
  }

  /** Freie id auf Basis des Wunschnamens (zweite Widget-Kopie auf der Seite). */
  function freeId(base) {
    var id = base;
    var n = 1;
    while (document.getElementById(id) && n < 20) {
      n += 1;
      id = base + '--' + n;
    }
    return id;
  }

  function repairJudgeMeTabs() {
    var lists = document.querySelectorAll('.jdgm-tablist');

    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      var scope = widgetScope(list);
      var panel = findPanel(list, scope);
      var tabs = list.querySelectorAll('[role="tab"]');
      var j;
      var tab;
      var wanted;
      var target;

      // Durchgang 1: Originalwert sichern und den AKTIVEN Reiter mit einem
      // echten Bereich verbinden. Muss vor dem Aufraeumen laufen, sonst haengt
      // der zuvor aktive Reiter fuer einen Frame in der Luft.
      for (j = 0; j < tabs.length; j++) {
        tab = tabs[j];
        wanted = wantedPanelId(tab);
        if (!wanted) continue;

        if (tab.getAttribute('data-ap-tabpanel') !== wanted) {
          write(tab.setAttribute.bind(tab, 'data-ap-tabpanel', wanted));
        }
        if (tab.getAttribute('aria-selected') !== 'true') continue;

        target = document.getElementById(wanted);
        if (target && (!scope || scope.contains(target))) {
          // Ziel existiert und gehoert zu diesem Widget (App repariert oder von
          // uns gesetzt) — nur den Verweis wieder anhaengen, falls wir ihn
          // frueher entfernt hatten.
          if (tab.getAttribute('aria-controls') !== wanted) {
            write(tab.setAttribute.bind(tab, 'aria-controls', wanted));
          }
        } else if (panel) {
          // `target` ausserhalb dieses Widgets = zweite Widget-Kopie auf der
          // Seite. Dann eine eigene id vergeben, statt eine doppelte id zu
          // erzeugen oder auf ein fremdes Widget zu zeigen.
          adoptPanel(panel, tab, target ? freeId(wanted) : wanted);
        }
      }

      // Durchgang 2: Verweise entfernen, die jetzt ins Leere oder in ein
      // FREMDES Widget zeigen. Bei `role="tab"` ist `aria-controls` optional;
      // `data-ap-tabpanel` haelt den Wert fuer die spaetere Aktivierung.
      for (j = 0; j < tabs.length; j++) {
        tab = tabs[j];
        wanted = tab.getAttribute('aria-controls');
        if (!wanted) continue;
        target = document.getElementById(wanted);
        if (!target || (scope && !scope.contains(target))) {
          write(tab.removeAttribute.bind(tab, 'aria-controls'));
        }
      }
    }
  }

  function adoptPanel(panel, tab, wanted) {
    // Fremde id nicht ueberschreiben — nur ein leeres oder ein von uns selbst
    // vergebenes Feld wird neu belegt.
    var own = panel.getAttribute('data-ap-tabpanel-owner') === '1';
    if (panel.id && !own) return;

    if (panel.id !== wanted) {
      write(function () {
        panel.id = wanted;
        panel.setAttribute('data-ap-tabpanel-owner', '1');
      });
    }
    if (panel.getAttribute('role') !== 'tabpanel') {
      write(panel.setAttribute.bind(panel, 'role', 'tabpanel'));
    }
    if (tab.id && panel.getAttribute('aria-labelledby') !== tab.id) {
      write(panel.setAttribute.bind(panel, 'aria-labelledby', tab.id));
    }
    if (tab.getAttribute('aria-controls') !== wanted) {
      write(tab.setAttribute.bind(tab, 'aria-controls', wanted));
    }
  }

  function run() {
    scheduled = false;
    try {
      repairJudgeMeTabs();
    } catch (e) {
      /* Eine Reparatur darf niemals die Seite mitreissen. */
    }
    if (writes >= WRITE_BUDGET) stopObservers();
  }

  /*
   * KOSTEN-UMBAU (PageSpeed): Die alte Schale observierte das GESAMTE Dokument
   * inkl. 'class' im attributeFilter und lief per requestAnimationFrame — bei
   * der Vue-Hydration des Widgets, jedem Slider-Tick und jedem Drawer-Update
   * feuerte die Reparatur bis zu 60x/s ueber den kompletten DOM. Lighthouse
   * rechnete der Datei damit 240-320 ms Scripting und Long Tasks bis 319 ms zu.
   * Jetzt: (a) Start erst nach window.load + Idle — vorher existiert nichts zu
   * reparieren (Judge.me rendert die Reiterleiste selbst erst danach; axe wie
   * die Shopify-Agenten-Pruefung messen nach Netz-Idle). (b) Ein billiger
   * childList-only-Waechter auf dem Dokument prueft NUR eingefuegte Teilbaeume
   * auf Widget-Wurzeln. (c) Je Widget-Wurzel ein eng gescopter Observer OHNE
   * 'class' — der Reiterwechsel kommt als aria-selected-Mutation durch ihn.
   * (d) Debounce statt rAF: die ARIA-Reparatur ist nicht frame-kritisch.
   */
  var DEBOUNCE_MS = 120;
  var ROOT_SEL = '.jdgm-tablist, .jm-review-widget, .jdgm-review-widget';
  var seenRoots = typeof WeakSet === 'function' ? new WeakSet() : null;
  var scopedObservers = [];
  var rootWatcher = null;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(run, DEBOUNCE_MS);
  }

  function stopObservers() {
    if (rootWatcher) rootWatcher.disconnect();
    for (var i = 0; i < scopedObservers.length; i++) scopedObservers[i].disconnect();
    scopedObservers.length = 0;
  }

  /** Aeusserste Widget-Huelle als Observer-Wurzel (deckt alle Tablists darin ab). */
  function rootFor(el) {
    return (
      el.closest('.jdgm-review-widget') ||
      el.closest('.jm-review-widget') ||
      el.closest('.jm-review-widget__header-and-body') ||
      el
    );
  }

  function observeRoot(el) {
    if (!el || !window.MutationObserver) return;
    if (seenRoots) {
      if (seenRoots.has(el)) return;
      seenRoots.add(el);
    }
    var o = new MutationObserver(schedule);
    o.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected', 'aria-controls', 'id'],
    });
    scopedObservers.push(o);
  }

  /**
   * Nur den EINGEFUEGTEN Teilbaum pruefen — nie das Dokument scannen.
   * querySelectorAll statt querySelector: ein per innerHTML ersetzter
   * Container (Warenkorb-Drawer) kann MEHRERE Widget-Wurzeln auf einmal
   * mitbringen; nur die erste zu observieren liesse die zweite bei spaeteren
   * Reiterwechseln unrepariert.
   */
  function checkAdded(node) {
    if (!node || node.nodeType !== 1 || !node.matches) return false;
    var found = false;
    if (node.matches(ROOT_SEL)) {
      observeRoot(rootFor(node));
      found = true;
    }
    var hits = node.querySelectorAll(ROOT_SEL);
    for (var i = 0; i < hits.length; i++) {
      observeRoot(rootFor(hits[i]));
      found = true;
    }
    return found;
  }

  function onDomAdditions(records) {
    var found = false;
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var k = 0; k < added.length; k++) {
        if (checkAdded(added[k])) found = true;
      }
    }
    if (found) schedule();
  }

  function start() {
    var existing = document.querySelectorAll(ROOT_SEL);
    for (var i = 0; i < existing.length; i++) observeRoot(rootFor(existing[i]));
    if (existing.length) run();

    // Spaete Kopien finden (Warenkorb-Schubkasten haengt das Widget nach jedem
    // Update neu ein) — childList-only, ohne Attribut-Lauschen auf dem Dokument.
    if (window.MutationObserver) {
      rootWatcher = new MutationObserver(onDomAdditions);
      rootWatcher.observe(document.documentElement, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', schedule);
    // Phase D Teil 2 (P5): spaet geladene Anbieter melden sich mit ap:spaet-geladen — einmal neu pruefen.
    document.addEventListener('ap:spaet-geladen', function () {
      var spaete = document.querySelectorAll(ROOT_SEL);
      for (var s = 0; s < spaete.length; s++) observeRoot(rootFor(spaete[s]));
      schedule();
    });
  }

  function startWhenIdle() {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(start, { timeout: 2000 });
    } else {
      setTimeout(start, 200);
    }
  }

  if (document.readyState === 'complete') {
    startWhenIdle();
  } else {
    window.addEventListener('load', startWhenIdle, { once: true });
  }
})();

/**
 * ---------------------------------------------------------------------------
 * 2) Judge.me — Reiter-Dialog verdeckt das Schreib-Modal
 * ---------------------------------------------------------------------------
 * Der schwebende Bewertungs-Reiter (.jdgm-revs-tab-btn) oeffnet den
 * Alle-Bewertungen-Dialog `.jdgm-modal.jdgm-widget-revamp` mit z-index 999999.
 * Der Knopf "Bewertung schreiben" DARIN oeffnet Judge.mes Schreib-Modal
 * `.jdgm-write-review-modal` — mit z-index 1100, also HINTER dem Dialog.
 *
 * Live gemessen am 2026-09-02 (Playwright, reifenfair.de Startseite):
 *   .jdgm-modal.jdgm-widget-revamp   z-index 999999   bleibt oben
 *   .jdgm-write-review-modal         z-index 1100     oeffnet dahinter
 *   elementFromPoint(Mitte)          weiterhin der Dialog-Inhalt
 * Fuer den Kunden passiert nach dem Klick sichtbar NICHTS — das Formular ist
 * offen, aber vollstaendig verdeckt ("sieht kaputt aus").
 *
 * Zwei Reparaturen, beide noetig:
 *   a) Z-INDEX-NETZ (CSS, injiziert): das Schreib-Modal ueber den Dialog
 *      heben. Damit ist der Fehler behoben, selbst wenn b) ins Leere greift.
 *   b) DIALOG SCHLIESSEN (Verhalten): oeffnet sich das Schreib-Modal aus dem
 *      Dialog heraus, wird der Dialog geschlossen — der Kunde soll nach dem
 *      Absenden nicht in einer Modal-Zwiebel stehen. Verhaltensbasiert statt
 *      knopfbasiert: NACH jedem Klick im Dialog wird kurz geprueft, ob das
 *      Schreib-Modal sichtbar wurde (sprach- und klassenunabhaengig; ein
 *      Klick auf "Weitere laden" o. ae. schliesst nichts).
 *
 * Schliess-Reihenfolge defensiv: erst Judge.mes eigener Schliessen-Knopf
 * (haelt deren Vue-Zustand konsistent), dann der Overlay-Klick, zuletzt
 * hartes Ausblenden. Selbstentschaerfend: hebt Judge.me die z-Reihenfolge
 * selbst ins Lot und schliesst den Dialog kuenftig selbst, wird das
 * Schreib-Modal hier nie mehr als verdeckt erkannt.
 */
(function () {
  'use strict';

  var DIALOG = '.jdgm-modal.jdgm-widget-revamp';
  var WRITE = '.jdgm-write-review-modal';

  // a) Z-Index-Netz. !important, weil Judge.mes eigene Regel spezifischer
  //    laedt; der Wert liegt EINEN ueber dem gemessenen Dialog-z-index.
  var style = document.createElement('style');
  style.textContent = DIALOG + ' { z-index: 999999; } ' + WRITE + ' { z-index: 1000000 !important; }';
  document.head.appendChild(style);

  function sichtbar(el) {
    if (!el) return false;
    var st = getComputedStyle(el);
    if (st.display === 'none' || st.visibility === 'hidden') return false;
    var r = el.getBoundingClientRect();
    return r.width > 100 && r.height > 100;
  }

  function schliesseDialog(dialog) {
    if (!dialog || !sichtbar(dialog)) return;
    // 1. Judge.mes eigener Schliessen-Knopf (Vue-Zustand bleibt richtig).
    var knoepfe = dialog.querySelectorAll('button, a');
    for (var i = 0; i < knoepfe.length; i++) {
      var k = knoepfe[i];
      var label = (k.getAttribute('aria-label') || '') + ' ' + (k.className || '');
      if (/close|schlie/i.test(label)) {
        k.click();
        if (!sichtbar(dialog)) return;
      }
    }
    // 2. Overlay-Klick (uebliches Schliessverhalten der Bibliothek).
    var overlay = document.querySelector('.jdgm-modal__overlay');
    if (overlay) {
      overlay.click();
      if (!sichtbar(dialog)) return;
    }
    // 3. Hartes Ausblenden — letzter Ausweg, besser als eine Modal-Zwiebel.
    dialog.style.setProperty('display', 'none', 'important');
  }

  function pruefe() {
    var write = document.querySelector(WRITE);
    if (!sichtbar(write)) return;
    schliesseDialog(document.querySelector(DIALOG));
  }

  document.addEventListener(
    'click',
    function (event) {
      var t = event.target;
      if (!t || typeof t.closest !== 'function') return;
      if (!t.closest(DIALOG)) return;
      // Zwei Pruefzeitpunkte: sofortiges und traeges Oeffnen des Schreib-Modals.
      setTimeout(pruefe, 150);
      setTimeout(pruefe, 600);
    },
    true
  );
})();

/**
 * ---------------------------------------------------------------------------
 * 3) Judge.me — schwebender Bewertungs-Reiter mit englischem Namen
 * ---------------------------------------------------------------------------
 * Live gemessen am 2026-09-24 (Headless-Chrome, reifenfair.de Startseite,
 * 390x844, nach der Zustimmung), vollstaendiges Markup des Reiters:
 *
 *   <div class="jdgm-revs-tab-btn btn" position="right" tabindex="0"
 *        aria-label="Click to open Judge.me floating reviews tab"
 *        role="button" data-style="text" style="...">★ Bewertungen</div>
 *
 * Sichtbar steht „★ Bewertungen“, vorgelesen wird ein englischer Satz. Das
 * ist ein Sprachbruch auf einer deutschen Seite (axe auf allen fuenf
 * Seitentypen) und verletzt „Label in Name“ (WCAG 2.5.3): wer per
 * Sprachsteuerung „Bewertungen“ sagt, trifft den Knopf nicht, weil das Wort
 * in seinem Namen fehlt. role="button" und tabindex="0" setzt Judge.me
 * selbst richtig — daran aendert sich nichts.
 *
 * Reparatur: NUR das aria-label, und zwar auf den deutschen Text aus der
 * Locale (ap.navmisc.reviews_tab_label = „Bewertungen öffnen“, enthaelt das
 * sichtbare Wort). Eine Asset-Datei kann keine Locale lesen; sections/
 * header.liquid liefert den Text als data-ap-reviews-tab-label am Kopf.
 * Die Farbe (weiss auf Gelb, 1,54:1) ist eine Judge.me-Einstellung des
 * Betreibers je Shop und bleibt hier bewusst unangetastet.
 *
 * Judge.me haengt den Reiter spaet ein. Ein childList-Waechter sucht NUR in
 * eingefuegten Teilbaeumen und gibt nach 30 s auf; am gefundenen Knopf
 * haelt ein Attribut-Waechter das Label, falls die App es zurueckschreibt
 * (mit Schreibbudget gegen eine Endlosschleife). Selbstentschaerfend: steht
 * das gewuenschte Label schon da, wird nichts geschrieben.
 */
(function () {
  'use strict';

  var TAB = '.jdgm-revs-tab-btn';
  var FALLBACK = 'Bewertungen öffnen'; // TODO i18n: nur Rueckfall, der Text kommt aus der Locale
  var GIVE_UP_MS = 30000;
  var budget = 40;
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

  function wantedLabel() {
    var source = document.querySelector('[data-ap-reviews-tab-label]');
    var text = source ? (source.getAttribute('data-ap-reviews-tab-label') || '').trim() : '';
    return text || FALLBACK;
  }

  function relabel(tab) {
    var want = wantedLabel();
    if (tab.getAttribute('aria-label') === want) return;
    if (budget <= 0) return;
    budget -= 1;
    tab.setAttribute('aria-label', want);
  }

  function adopt(tab) {
    relabel(tab);
    if (!window.MutationObserver) return;
    if (seen) {
      if (seen.has(tab)) return;
      seen.add(tab);
    }
    new MutationObserver(function () {
      relabel(tab);
    }).observe(tab, { attributes: true, attributeFilter: ['aria-label'] });
  }

  function scan(node) {
    if (!node || node.nodeType !== 1 || !node.matches) return 0;
    var found = 0;
    if (node.matches(TAB)) {
      adopt(node);
      found += 1;
    }
    var inner = node.querySelectorAll(TAB);
    for (var i = 0; i < inner.length; i++) {
      adopt(inner[i]);
      found += 1;
    }
    return found;
  }

  var watcher = null;
  var giveUp = 0;

  function wache() {
    if (!window.MutationObserver) return;
    if (watcher) watcher.disconnect();
    watcher = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var k = 0; k < added.length; k++) scan(added[k]);
      }
    });
    watcher.observe(document.body, { childList: true, subtree: true });
    clearTimeout(giveUp);
    giveUp = setTimeout(function () {
      watcher.disconnect();
      watcher = null;
    }, GIVE_UP_MS);
  }

  function start() {
    scan(document.body);
    wache();
    // Phase D Teil 2 (P5): ein SPAET geladener Anbieter (assets/ap-spaet.js, Ereignis ap:spaet-geladen)
    // haengt den Reiter womoeglich erst nach den 30 s ein — dann noch einmal suchen und 30 s wachen.
    document.addEventListener('ap:spaet-geladen', function () {
      scan(document.body);
      wache();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/**
 * ---------------------------------------------------------------------------
 * 4) Judge.me — Karten ausserhalb des sichtbaren Karussell-Bereichs (Q-12)
 * ---------------------------------------------------------------------------
 * Gemessen am 08.10.2026 (Messvorschau 208313680211, Startseite, Chromium,
 * T2/bau-spaet-arbeit/erkunden/erkunden.json): das Karten-Karussell im Fuss
 * (.jdgm-cards-carousel, Footer-Gruppe auf jeder Seite) haelt 28 Karten samt
 * Klonen (jdgm-clone-before/-after) in EINER Leiste, die Judge.me per
 * transform verschiebt; .jdgm-cards-wrapper schneidet mit overflow: hidden.
 * Sichtbar sind 1 Karte (390 px) bzw. 4 (1440 px) — fokussierbar aber alle
 * 28 Kartenlinks. Die Tab-Taste wandert durch 27 bzw. 24 unsichtbare Karten
 * (m0-qualitaet Q-12: 705 Fokus-Stopps auf 50 Seiten; WCAG 2.4.3, 2.4.7).
 *
 * Reparatur: ein IntersectionObserver mit dem schneidenden Wrapper als Wurzel
 * setzt jede Karte, die zu weniger als der Haelfte im sichtbaren Bereich
 * liegt, auf `inert` (nicht fokussierbar, nicht im Vorlesebaum); kommt sie ins
 * Bild — Pfeil, Wischen, Autoplay —, faellt `inert` wieder. Solange der Fuss
 * nicht gerendert ist (content-visibility: auto; Meldung 0 x 0), bleibt alles
 * wie es ist — vor dem ersten Rendern also ohne `inert`. Danach meldet der
 * Beobachter nur Karten, deren Lage sich AENDERT; Karten, die ausserhalb
 * bleiben, meldete er nie wieder (Pflicht-Aufnahme q12 08.10.: 0 inert, 4
 * unsichtbare Kartenstopps). Darum: kommt das Karussell ins Bild (= gerendert)
 * oder kommt nach dem Ueberspringen die erste echte Meldung, werden nach einem
 * Bild alle Karten neu beobachtet — frische erste Meldung fuer jede, einmal je
 * Ueberspringen. Die Pfeile liegen
 * ausserhalb des Wrappers und bleiben bedienbar. Ohne `inert`-Unterstuetzung
 * bekommen die Links der Karte tabindex="-1" (der alte Wert wird gemerkt).
 * Neue Karten (Judge.me baut sie per Skript, auch spaet geladen) findet ein
 * childList-Waechter NUR am Karussell. Nur Attribute, die wir selbst gesetzt
 * haben, nehmen wir zurueck (data-ap-q12).
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  var KARUSSELL = '.jdgm-cards-carousel';
  var WRAPPER = '.jdgm-cards-wrapper';
  var KARTE = '.jdgm-carousel-item';
  var FOKUS = 'a[href], button, input, select, textarea, [tabindex]';
  var MIN_ANTEIL = 0.5;
  var hatInert = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;
  var gebunden = typeof WeakMap === 'function' ? new WeakMap() : null;
  var stand = typeof WeakMap === 'function' ? new WeakMap() : null;

  function aussen(karte, an) {
    var markiert = karte.hasAttribute('data-ap-q12');
    if (an === markiert) return;
    if (an) {
      karte.setAttribute('data-ap-q12', '');
      if (hatInert) karte.setAttribute('inert', '');
      else {
        var ziele = karte.querySelectorAll(FOKUS);
        for (var i = 0; i < ziele.length; i++) {
          var z = ziele[i];
          if (z.hasAttribute('data-ap-q12-tab')) continue;
          z.setAttribute('data-ap-q12-tab', z.getAttribute('tabindex') === null ? '' : z.getAttribute('tabindex'));
          z.setAttribute('tabindex', '-1');
        }
      }
    } else {
      karte.removeAttribute('data-ap-q12');
      if (hatInert) karte.removeAttribute('inert');
      else {
        var alte = karte.querySelectorAll('[data-ap-q12-tab]');
        for (var j = 0; j < alte.length; j++) {
          var a = alte[j];
          var wert = a.getAttribute('data-ap-q12-tab');
          a.removeAttribute('data-ap-q12-tab');
          if (wert === '') a.removeAttribute('tabindex');
          else a.setAttribute('tabindex', wert);
        }
      }
    }
  }

  function binde(karussell) {
    var wurzel = karussell.querySelector(WRAPPER);
    if (!wurzel) return false;
    var eintrag = gebunden ? gebunden.get(karussell) : null;
    if (!eintrag || eintrag.wurzel !== wurzel) {
      if (eintrag) eintrag.io.disconnect();
      var neu = {
        wurzel: wurzel,
        gesehen: typeof WeakSet === 'function' ? new WeakSet() : null,
        offen: false,
        geplant: false,
        io: null,
      };
      neu.io = new IntersectionObserver(
        function (eintraege) {
          for (var i = 0; i < eintraege.length; i++) {
            var e = eintraege[i];
            // Uebersprungener Fuss (content-visibility: auto, ap-defer-render): der Beobachter meldet dann
            // Wurzel und Karte mit 0 x 0 (gemessen 08.10.). Das ist KEIN Wissen ueber die Lage — nichts aendern,
            // aber merken: nach dem Rendern neu messen (frisch), sonst bleiben Karten ausserhalb fokussierbar.
            if (!e.rootBounds || (!e.rootBounds.width && !e.rootBounds.height)) {
              neu.offen = true;
              continue;
            }
            // Erste echte Meldung nach einem Ueberspringen = der Fuss ist gerendert: auch dann neu messen
            // (zweites Signal neben dem Sicht-Beobachter unten; frisch() laeuft je Ueberspringen nur einmal).
            if (neu.offen && !neu.geplant) {
              neu.geplant = true;
              window.requestAnimationFrame(function () {
                neu.geplant = false;
                frisch(karussell);
              });
            }
            var weg = !e.isIntersecting || e.intersectionRatio < MIN_ANTEIL;
            if (stand) stand.set(e.target, weg);
            // Laeuft die Karte weg, waehrend sie den Fokus haelt (Autoplay), bleibt sie bedienbar,
            // bis der Fokus sie verlaesst (focusout unten) — nie den Fokus ins Leere fallen lassen.
            if (weg && e.target.contains(document.activeElement)) continue;
            aussen(e.target, weg);
          }
        },
        { root: wurzel, threshold: [0, MIN_ANTEIL, 1] }
      );
      eintrag = neu;
      if (gebunden) gebunden.set(karussell, eintrag);
    }
    var karten = karussell.querySelectorAll(KARTE);
    for (var k = 0; k < karten.length; k++) {
      if (eintrag.gesehen) {
        if (eintrag.gesehen.has(karten[k])) continue;
        eintrag.gesehen.add(karten[k]);
      }
      eintrag.io.observe(karten[k]);
    }
    return karten.length > 0;
  }

  /** Nach einem Ueberspringen (0 x 0) alle Karten neu beobachten: frische erste Meldung fuer jede. */
  function frisch(karussell) {
    var eintrag = gebunden ? gebunden.get(karussell) : null;
    if (!eintrag || !eintrag.offen) return;
    eintrag.offen = false;
    var karten = karussell.querySelectorAll(KARTE);
    for (var k = 0; k < karten.length; k++) {
      eintrag.io.unobserve(karten[k]);
      eintrag.io.observe(karten[k]);
    }
  }

  function wache(karussell) {
    binde(karussell);
    if (karussell.__apQ12) return;
    karussell.__apQ12 = true;
    karussell.addEventListener('focusout', function (ev) {
      var karte = ev.target && typeof ev.target.closest === 'function' ? ev.target.closest(KARTE) : null;
      if (!karte || (ev.relatedTarget && karte.contains(ev.relatedTarget))) return;
      if (stand && stand.get(karte) === true) aussen(karte, true);
    });
    // Im Bild = gerendert: nach einem Bild neu messen, falls der Fuss vorher uebersprungen war.
    new IntersectionObserver(function (eintraege) {
      for (var i = 0; i < eintraege.length; i++) {
        if (!eintraege[i].isIntersecting) continue;
        window.requestAnimationFrame(function () {
          frisch(karussell);
        });
        return;
      }
    }).observe(karussell);
    if (!window.MutationObserver) return;
    var geplant = false;
    new MutationObserver(function () {
      if (geplant) return;
      geplant = true;
      // Judge.me baut Karten und Klone in einem Schub — einmal je Bild nachziehen.
      window.requestAnimationFrame(function () {
        geplant = false;
        binde(karussell);
      });
    }).observe(karussell, { childList: true, subtree: true });
  }

  function start() {
    var liste = document.querySelectorAll(KARUSSELL);
    for (var i = 0; i < liste.length; i++) wache(liste[i]);
  }

  document.addEventListener('ap:spaet-geladen', start);
  document.addEventListener('shopify:section:load', start);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
